<script setup lang="ts">
import { ref, watch, computed, getCurrentInstance } from "vue";

interface SelectOption {
	label: string;
	value: string;
}

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  options: {
    type: Array,
    required: true,
  },
  position: {
    type: String,
    default: '',
  },
  dynamicPosition: {
    type: Boolean,
    default: false,
  },
  dynamicWidth: {
    type: Boolean,
    default: false,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  light: {
    type: Boolean,
    default: false,
  },
  dark: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  langKeys: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});
const instanceId = getCurrentInstance()?.uid || Math.floor(Math.random() * (99999 - 10000 + 1)) + 10000; // with fallback

const emit = defineEmits(["select", "opened"]);

const selectValue = ref(props.modelValue);
const selectOpened = ref(false);

watch(props, async () => {
  if (props.modelValue != selectValue.value) {
    selectValue.value = props.modelValue;
  }
});

watch(selectOpened, (val) => {
  emit("opened", val);
});

// watch(selectValue, () => {
//   if (Array.isArray(selectValue.value)) {
//     const hasSelectAll = selectValue.value.some(item => item.value === 'SELECTALL');
//     if (hasSelectAll) {
//       selectAllOptions()
//     }
//   }

// 	emit("select", selectValue.value);
// });

// const optionList = computed(() => {
// 	const selectAllItem = new Array({
// 		label: 'Select all',
// 		value: 'SELECTALL'
// 	});
// 	const options = props.multiple ? props.options.concat(selectAllItem) : props.options;
// 	return options;
// });

const dynWidth = computed(() => {
  let maxChars = 0;
  for (const option of props.options) {
    const labelLength = typeof option === "string" ? option.length : (option as SelectOption)?.label?.length ?? 0;
    if (labelLength > maxChars) {
      maxChars = labelLength;
    }
  }

  if (maxChars < 10) {
    return null;
  }

  return `${maxChars*14+50}px`;
});

const mainClasses = computed(() => {
  let classList = "";
  classList += props.position == 'center' ? "v-select--on-center-list " : "";
  classList += props.position == 'right' ? "v-select--on-right-list " : "";
  classList += props.multiple ? "v-select--multiple " : "";
  classList += props.light ? "v-select--light " : "";
  classList += props.dark && !props.light ? "v-select--dark " : "";
  classList += props.compact ? "v-select--compact " : "";
  return classList
});

function onSelectUpdate(item: any) {
  // if (Array.isArray(selectValue.value)) {
  //   const hasSelectAll = selectValue.value.some(item => item.value === 'SELECTALL');
  //   if (hasSelectAll) {
  //     selectAllOptions()
  //   }
  // }

	emit("select", selectValue.value);
}

function isChecked(option: SelectOption) {
  if (!Array.isArray(selectValue.value)) {
    return false;
  }

	return selectValue.value.some(item => item.value === option.value);
}

function toggleOption(option: SelectOption) {
  if (!Array.isArray(selectValue.value)) {
    return false;
  }
  
	const index = selectValue.value.findIndex(item => item.value === option.value);
	if (index > -1) {
		let newSelected = selectValue.value;
		newSelected.splice(index, 1);
		selectValue.value = newSelected;
		emit("select", selectValue.value);
	} else {
		selectValue.value.push(option);
		emit("select", selectValue.value);
	}
}

function calcPosition(
  dropdownList: HTMLElement,
  component: any,
  { width }: { width: string }
) {
  if (component.dynamicPosition === false) return;

  const referenceElement = component.$refs.toggle as HTMLElement;
  if (!referenceElement) return;

  dropdownList.style.width =
    props.dynamicWidth && dynWidth.value ? dynWidth.value : width;

  dropdownList.style.position = 'absolute';
  dropdownList.style.visibility = 'hidden';
  dropdownList.style.display = 'block';

  dropdownList.classList.add('dynamic-dropdown');
  dropdownList.classList.remove('animate-dropdown', 'drop-up');

  function calculatePosition(reference: HTMLElement) {
    const rect = reference.getBoundingClientRect();
    const dropdownRect = dropdownList.getBoundingClientRect();

    const spaceBelow = window.innerHeight - rect.bottom - 20;
    const spaceAbove = rect.top - 20;

    let top: number;
    let isDropUp = false;

    if (spaceBelow >= dropdownRect.height) {
      top = rect.top + window.scrollY + rect.height;
    } else if (spaceAbove >= dropdownRect.height) {
      top = rect.top + window.scrollY - dropdownRect.height;
      isDropUp = true;
    } else {
      if (spaceBelow >= spaceAbove) {
        top = rect.top + window.scrollY + rect.height;
      } else {
        top = rect.top + window.scrollY - dropdownRect.height;
        isDropUp = true;
      }
    }

    if (top < 10) top = 10;

    const tentativeLeft = rect.left + window.scrollX;
    const dropdownRight = tentativeLeft + dropdownRect.width;
    const maxRight = window.innerWidth - 10;

    let left =
      dropdownRight > maxRight
        ? maxRight - dropdownRect.width
        : tentativeLeft;

    if (left < 10) left = 10;

    return { top, left, isDropUp };
  }

  const applyPosition = () => {
    const position = calculatePosition(referenceElement);

    dropdownList.style.top = `${position.top}px`;
    dropdownList.style.left = `${position.left}px`;

    dropdownList.classList.toggle('drop-up', position.isDropUp);

    // overflow only if actually needed
    dropdownList.style.maxHeight =
      window.innerHeight - 40 + 'px';

    dropdownList.style.overflowY =
      dropdownList.scrollHeight >
      window.innerHeight - 40 ? 'auto' : '';
  };

  requestAnimationFrame(() => {
    applyPosition();

    dropdownList.style.visibility = 'visible';
    dropdownList.style.display = '';

    requestAnimationFrame(() => {
      dropdownList.classList.add('animate-dropdown');
    });
  });

  const updatePosition = () => {
    applyPosition();
  };

  window.addEventListener('resize', updatePosition);
  window.addEventListener('scroll', updatePosition);

  return () => {
    window.removeEventListener('resize', updatePosition);
    window.removeEventListener('scroll', updatePosition);
  };
}

</script>
<template>
  <Teleport to="body" :disabled="!dynamicPosition">
    <Transition name="opacity">
      <div v-show="selectOpened" class="v-select__custom-background"></div>
    </Transition>
  </Teleport>
  <v-select
    v-model="selectValue"
    @update:modelValue="onSelectUpdate"
    @open="selectOpened = true"
    @close="selectOpened = false"
    :options="options"
    :searchable="searchable && !multiple"
    :clearable="false"
    :class="mainClasses"
    :id="`v-select-${instanceId}`"
    :multiple="multiple"
    data-nofocus
    :appendToBody="dynamicPosition"
    :calculatePosition="calcPosition"
    :style="dynamicWidth ? `width:${dynWidth};` : ''"
    :disabled="disabled"
  >
  <template v-if="langKeys" #selected-option="{ label }">
    {{ $t(label) }}
  </template>
  <template v-if="multiple" v-slot:option="option">
    <!-- <span v-if="option.value === 'SELECTALL'" class="option--select-all">{{ $t('text.button_select_all') }}</span>
     <span v-else> -->
    <span>
      <span class="form-item form-item--checkbox-tick">
        <input :id="`${option.value}-checkbox-tick`" type="checkbox" :checked="isChecked(option)" />
        <label class="toggle" :for="`${option.value}-checkbox-tick`" />
        <button @click.stop="toggleOption(option)" class="button--toggle-tick"></button>
      </span>
      <span class="label">
        <template v-if="langKeys">{{ $t(option.label) }}</template>
        <template v-else>{{ option.label }}</template>
      </span>
    </span>
  </template>
  <template v-else v-slot:option="option">
    <span class="label">
      <template v-if="langKeys">{{ $t(option.label) }}</template>
      <template v-else>{{ option.label }}</template>
    </span>
  </template>
  <template v-slot:no-options>
    <span class="v-select__no-options">{{ $t('form.no_options_found') }}</span>
  </template>
</v-select>
		<!-- :dropdown-should-open="() => true" -->
</template>